import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, YesNoSelect, HybridSelect, DateField } from "@/components/forms/fields";
import PhoneInput from "@/components/forms/PhoneInput";
import { etiquetaTipoDispositivo } from "@/lib/dispositivoLabel";
import ContactForm from "@/components/forms/ContactForm";
import { ReadinessPanel } from "@/components/admin/readiness";
import { Trash2, Plus, Check } from "lucide-react";

const STEPS = ["Datos", "Contactos e instrucciones", "Dispositivos", "Revisión"];

const siNo = (v) => (v === true || v === "si" ? "Sí" : v === false || v === "no" ? "No" : "");

// UX-005: fila del resumen previo. No muestra secretos: sólo lo que el operador capturó aquí.
function ResumenFila({ label, valor, testId }) {
  return (
    <div className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-start sm:gap-4">
      <span className="text-xs text-slate-400 sm:w-56 shrink-0">{label}</span>
      <span data-testid={testId} className={`text-sm whitespace-pre-line break-words ${valor ? "text-[#012472]" : "text-slate-400 italic"}`}>
        {valor || "Sin capturar"}
      </span>
    </div>
  );
}

const empty = () => ({
  nombre_completo: "", fecha_nacimiento: "", telefono: "", idioma_preferido: "Español",
  paquete: "", habitacion: "", condiciones: "", estado: "Activa",
  clientes: [], contactos: [],
  instrucciones: {
    quien_modifica_protocolo: { contacto_id: null, nombre: "", telefono: "" },
    autorizacion_emergencia: { valor: null, fecha: "" },
    confirmacion_limites: { valor: null, fecha: "" },
    consentimiento_audio_bidireccional: { valor: null, fecha: "" },
  },
});

// Wizard guiado de ALTA (v2.9.8). 4 pasos. Permite guardar una persona incompleta (borrador);
// la pantalla final muestra las tres dimensiones y faltantes desde el backend. NUNCA etiqueta
// como 'Listo' algo que el backend no considere listo (plan se configura por separado).
export default function PersonaWizard({ open, onOpenChange, sitioId, sitioNombre = "", dispositivos, clientes, contactos, onSaved, refetchContactos, modalidad = "residencial" }) {
  // UI-014: en un sitio residencial el alta no muestra campos con lenguaje institucional.
  const esInstitucional = modalidad === "institucional";
  const [step, setStep] = useState(0);
  const [f, setF] = useState(empty());
  const [selDevices, setSelDevices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [contactoDlg, setContactoDlg] = useState(false);
  const [contactosNuevos, setContactosNuevos] = useState([]);
  const [errorContactos, setErrorContactos] = useState("");

  const opcionesContactos = [
    ...(contactos || []),
    ...contactosNuevos.filter((n) => !(contactos || []).some((c) => c.id === n.id)),
  ];

  useEffect(() => {
    if (open) { setStep(0); setF(empty()); setSelDevices([]); setCreated(null); setContactosNuevos([]); setErrorContactos(""); }
  }, [open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setInstr = (k, v) => setF((p) => ({ ...p, instrucciones: { ...p.instrucciones, [k]: v } }));
  const cliRel = f.clientes?.[0] || { cliente_id: "", relacion_familiar: "", es_contratante_principal: true };
  const setCliRel = (patch) =>
    setF((p) => {
      const actual = p.clientes?.[0] || { cliente_id: "", relacion_familiar: "", es_contratante_principal: true };
      return { ...p, clientes: [{ ...actual, ...patch }] };
    });
  // Actualizadores funcionales: agregar y seleccionar en el mismo ciclo ya no se pisan (DATA-011).
  const addContactoRow = () =>
    setF((p) => ({
      ...p,
      contactos: [...(p.contactos || []), { contacto_id: "", relacion_familiar: "", orden: (p.contactos?.length || 0) + 1, puede_confirmar_estado: false }],
    }));
  const updContactoRow = (i, k, v) =>
    setF((p) => ({ ...p, contactos: (p.contactos || []).map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const delContactoRow = (i) =>
    setF((p) => ({ ...p, contactos: (p.contactos || []).filter((_, idx) => idx !== i) }));
  const vincularContactoNuevo = (nuevo) => {
    if (!nuevo?.id) return;
    setContactosNuevos((n) => [...n.filter((c) => c.id !== nuevo.id), nuevo]);
    setF((p) => {
      const filas = [...(p.contactos || [])];
      if (filas.some((r) => r.contacto_id === nuevo.id)) return p;
      const libre = filas.findIndex((r) => !r.contacto_id);
      if (libre >= 0) filas[libre] = { ...filas[libre], contacto_id: nuevo.id };
      else filas.push({ contacto_id: nuevo.id, relacion_familiar: "", orden: filas.length + 1, puede_confirmar_estado: false });
      return { ...p, contactos: filas };
    });
    setErrorContactos("");
    refetchContactos && refetchContactos();
  };
  const toggleDevice = (id) => setSelDevices((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    const incompletas = (f.contactos || []).filter((c) => !c.contacto_id).length;
    if (incompletas > 0) {
      setErrorContactos("Hay contactos sin seleccionar. Elígelos o elimina las filas antes de crear a la persona.");
      toast.error("Falta seleccionar el contacto en una de las filas.");
      setStep(1);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f, sitio_id: sitioId,
        clientes: cliRel.cliente_id ? [cliRel] : [],
        contactos: (f.contactos || []).map((c, i) => ({ ...c, orden: c.orden || i + 1 })),
      };
      const res = await api.post("/personas", payload);
      const pid = res.data.id;
      const updates = [];
      (dispositivos || []).forEach((d) => {
        if (selDevices.includes(d.id) && !(d.personas_monitoreadas || []).includes(pid)) {
          updates.push(api.put(`/dispositivos/${d.id}`, { ...d, personas_monitoreadas: [...(d.personas_monitoreadas || []), pid] }));
        }
      });
      if (updates.length) await Promise.all(updates);
      // Releer para reflejar readiness con dispositivos ya asignados.
      const site = await api.get(`/sitios/${sitioId}`);
      const fresh = (site.data.personas || []).find((x) => x.id === pid) || res.data;
      setCreated(fresh);
      toast.success("Persona creada.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl p-0 gap-0 max-h-[90vh] flex flex-col overflow-hidden" aria-describedby={undefined}>
        <div className="px-6 pt-6 pb-3 shrink-0">
          <DialogHeader>
            <DialogTitle className="text-[#012472]">Nueva persona — {STEPS[step]}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-1.5 mt-3" data-testid="wizard-steps">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#01CBE3]" : "bg-slate-200"}`} />
            ))}
          </div>
        </div>

        <div className="px-6 py-3 flex-1 min-h-0 overflow-y-auto" data-testid="wizard-body">

        {/* Paso 1 — Datos */}
        {step === 0 && (
          <div className="space-y-4">
            <Field label="Nombre completo">
              <Input data-testid="wizard-nombre" className="rounded-xl" value={f.nombre_completo} onChange={(e) => set("nombre_completo", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha de nacimiento"><DateField testId="wizard-fecha-nacimiento" value={f.fecha_nacimiento} onChange={(v) => set("fecha_nacimiento", v)} /></Field>
              <Field label="Idioma">
                <Select value={f.idioma_preferido} onValueChange={(v) => set("idioma_preferido", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Español">Español</SelectItem><SelectItem value="Inglés">Inglés</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Teléfono propio"><PhoneInput testId="wizard-telefono" value={f} onChange={(t) => setF((p) => ({ ...p, ...t }))} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Paquete">
                <Select value={f.paquete || ""} onValueChange={(v) => set("paquete", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent><SelectItem value="Básico">Básico</SelectItem><SelectItem value="Premium">Premium</SelectItem></SelectContent>
                </Select>
              </Field>
              {esInstitucional && (
                <Field label="Habitación / cama"><Input data-testid="wizard-habitacion" className="rounded-xl" value={f.habitacion} onChange={(e) => set("habitacion", e.target.value)} /></Field>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contratante / pagador">
                <Select value={cliRel.cliente_id || ""} onValueChange={(v) => setCliRel({ cliente_id: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Cliente" /></SelectTrigger>
                  <SelectContent>{(clientes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Parentesco"><Input className="rounded-xl" value={cliRel.relacion_familiar} onChange={(e) => setCliRel({ relacion_familiar: e.target.value })} /></Field>
            </div>
            <Field label="Condiciones que afectan la verificación (opcional)">
              <Textarea className="rounded-xl" value={f.condiciones} onChange={(e) => set("condiciones", e.target.value)} />
            </Field>
          </div>
        )}

        {/* Paso 2 — Contactos + instrucciones */}
        {step === 1 && (
          <div className="space-y-4">
            {/* UX-006: mínimo autoritativo del backend (readiness/plan de respuesta): 2. */}
            <p data-testid="wizard-contactos-minimo" className="text-xs text-slate-400">Mínimo requerido: 2 contactos de escalamiento.</p>
            {(f.contactos || []).map((row, i) => (
              <div key={i} className="rounded-xl border border-gray-100 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Contacto {i + 1}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => delContactoRow(i)}><Trash2 strokeWidth={1.5} className="h-4 w-4" /></Button>
                </div>
                <Select value={row.contacto_id || ""} onValueChange={(v) => updContactoRow(i, "contacto_id", v)}>
                  <SelectTrigger data-testid={`wizard-contacto-select-${i}`} className="rounded-xl"><SelectValue placeholder="Selecciona contacto" /></SelectTrigger>
                  <SelectContent>{opcionesContactos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre_completo || "(sin nombre)"}</SelectItem>)}</SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-2">
                  <Input className="rounded-xl" placeholder="Relación" value={row.relacion_familiar || ""} onChange={(e) => updContactoRow(i, "relacion_familiar", e.target.value)} />
                  <label className="flex items-center gap-2 text-sm text-slate-700 px-1">
                    <Checkbox checked={!!row.puede_confirmar_estado} onCheckedChange={(v) => updContactoRow(i, "puede_confirmar_estado", !!v)} /> Puede confirmar estado
                  </label>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-xl" onClick={addContactoRow} data-testid="wizard-add-contacto"><Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> Agregar contacto</Button>
              <Button variant="ghost" className="rounded-xl text-[#01CBE3]" data-testid="wizard-registrar-contacto" onClick={() => setContactoDlg(true)}>Registrar nuevo contacto</Button>
            </div>
            {errorContactos && <p data-testid="wizard-contactos-error" className="text-xs text-red-600">{errorContactos}</p>}
            <div className="rounded-xl border-2 border-[#01CBE3]/40 bg-[#01CBE3]/5 p-4">
              <p className="text-sm font-medium text-[#012472] mb-2">Titular del protocolo (autoriza cambios)</p>
              <HybridSelect value={f.instrucciones.quien_modifica_protocolo} onChange={(v) => setInstr("quien_modifica_protocolo", v)} contactos={opcionesContactos} testId="wizard-titular" />
            </div>
            <Field label="Autorización de emergencia">
              <YesNoSelect value={f.instrucciones.autorizacion_emergencia.valor} onChange={(v) => setInstr("autorizacion_emergencia", { ...f.instrucciones.autorizacion_emergencia, valor: v })} testId="wizard-autoriz-emergencia" />
            </Field>
            <Field label="Confirmación de límites del servicio">
              <YesNoSelect value={f.instrucciones.confirmacion_limites.valor} onChange={(v) => setInstr("confirmacion_limites", { ...f.instrucciones.confirmacion_limites, valor: v })} testId="wizard-limites" />
            </Field>
          </div>
        )}

        {/* Paso 3 — Dispositivos */}
        {step === 2 && (
          <div className="space-y-3">
            {/* UX-007: la relación persona↔dispositivo vive en `dispositivo.personas_monitoreadas`
                (PUT /dispositivos/{id}) y EXIGE que la persona ya exista, así que la selección de
                aquí se aplica al crearla. Sólo inventario del mismo sitio; no se crean equipos ni
                se amplían permisos desde este paso, y el paso nunca afirma cobertura terminada. */}
            {(dispositivos || []).length === 0 ? (
              <div data-testid="wizard-dispositivos-vacio" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-1.5">
                <p className="text-sm font-medium text-amber-900">Este sitio todavía no tiene equipos registrados.</p>
                <p className="text-xs text-amber-800">
                  Puedes continuar y crear a la persona: la asignación de dispositivos se completa
                  después, desde la sección <b>Dispositivos</b> del sitio. Hasta entonces la cobertura
                  de esta persona queda incompleta.
                </p>
              </div>
            ) : (
              <>
                <p data-testid="wizard-dispositivos-ayuda" className="text-xs text-slate-500">
                  Equipos ya registrados en este sitio. Los que marques quedarán monitoreando a la
                  persona en cuanto se cree; podrás cambiarlo después desde el sitio.
                </p>
                <div className="space-y-2">
                  {(dispositivos || []).map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox checked={selDevices.includes(d.id)} onCheckedChange={() => toggleDevice(d.id)} data-testid={`wizard-device-${d.id}`} />
                      {etiquetaTipoDispositivo(d.tipo)} — {d.ubicacion || "sin ubicación"}
                    </label>
                  ))}
                </div>
                {selDevices.length === 0 && (
                  <p data-testid="wizard-dispositivos-sin-seleccion" className="text-xs text-slate-400">
                    Si no marcas ninguno, la persona se crea sin equipos asignados y su cobertura
                    queda incompleta hasta que los asignes desde el sitio.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Paso 4 — Revisión */}
        {step === 3 && (
          <div className="space-y-4">
            {!created ? (
              <div className="space-y-4">
                {/* UX-005: revisión REAL antes de escribir. Se construye sólo con el estado local
                    del wizard: no hay GET, no se crea nada para poder mostrar el resumen. */}
                <div data-testid="wizard-revision-previa" className="rounded-xl border border-gray-100 divide-y divide-gray-100">
                  <ResumenFila label="Nombre completo" valor={f.nombre_completo} testId="resumen-nombre" />
                  <ResumenFila label="Fecha de nacimiento" valor={f.fecha_nacimiento} testId="resumen-fecha-nacimiento" />
                  <ResumenFila label="Teléfono propio" valor={f.telefono} testId="resumen-telefono" />
                  <ResumenFila label="Idioma" valor={f.idioma_preferido} testId="resumen-idioma" />
                  <ResumenFila label="Sitio" valor={`${sitioNombre || "Este sitio"} · ${esInstitucional ? "Institucional" : "Residencial"}`} testId="resumen-sitio" />
                  {esInstitucional && <ResumenFila label="Habitación / cama" valor={f.habitacion} testId="resumen-habitacion" />}
                  <ResumenFila label="Paquete (informativo)" valor={f.paquete} testId="resumen-paquete" />
                  <ResumenFila label="Contratante / pagador"
                    valor={cliRel.cliente_id ? `${(clientes || []).find((c) => c.id === cliRel.cliente_id)?.nombre_completo || cliRel.cliente_id}${cliRel.relacion_familiar ? ` · ${cliRel.relacion_familiar}` : ""}` : ""}
                    testId="resumen-contratante" />
                  <ResumenFila label="Condiciones que afectan la verificación" valor={f.condiciones} testId="resumen-condiciones" />
                  <ResumenFila label="Contactos de escalamiento"
                    valor={(f.contactos || []).length === 0 ? "" : (f.contactos || []).map((r, i) => `${i + 1}. ${opcionesContactos.find((c) => c.id === r.contacto_id)?.nombre_completo || "(sin seleccionar)"}${r.relacion_familiar ? ` · ${r.relacion_familiar}` : ""}${r.puede_confirmar_estado ? " · puede confirmar estado" : ""}`).join("\n")}
                    testId="resumen-contactos" />
                  <ResumenFila label="Titular del protocolo"
                    valor={f.instrucciones.quien_modifica_protocolo?.nombre || opcionesContactos.find((c) => c.id === f.instrucciones.quien_modifica_protocolo?.contacto_id)?.nombre_completo || ""}
                    testId="resumen-titular" />
                  <ResumenFila label="Autorización de emergencia" valor={siNo(f.instrucciones.autorizacion_emergencia?.valor)} testId="resumen-autoriz-emergencia" />
                  <ResumenFila label="Confirmación de límites del servicio" valor={siNo(f.instrucciones.confirmacion_limites?.valor)} testId="resumen-limites" />
                  <ResumenFila label="Dispositivos que se asignarán"
                    valor={selDevices.length === 0 ? "" : selDevices.map((id) => { const d = (dispositivos || []).find((x) => x.id === id); return `${etiquetaTipoDispositivo(d?.tipo)} — ${d?.ubicacion || "sin ubicación"}`; }).join("\n")}
                    testId="resumen-dispositivos" />
                </div>
                <div className="text-center space-y-3">
                  <p className="text-sm text-slate-500">Revisa los datos antes de guardar. Puede quedar como <b>borrador</b> si aún falta información; el plan de respuesta se configura por separado.</p>
                  <Button data-testid="wizard-crear-btn" disabled={saving} onClick={save} className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white">
                    {saving ? "Guardando…" : "Crear persona"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="wizard-revision">
                <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium"><Check className="h-4 w-4" /> Persona creada.</div>
                <ReadinessPanel readiness={created.readiness} testId="wizard-readiness" />
              </div>
            )}
          </div>
        )}

        </div>

        <div className="px-6 py-3 shrink-0 bg-white border-t border-gray-100 flex justify-between">
          <Button variant="outline" className="rounded-xl" onClick={step === 0 || created ? () => onOpenChange(false) : back}>
            {step === 0 || created ? "Cerrar" : "Atrás"}
          </Button>
          {!created && (
            step < STEPS.length - 1
              ? <Button data-testid="wizard-next" className="rounded-xl bg-[#012472] hover:bg-[#012472]/90 text-white" onClick={next}>Siguiente</Button>
              : null
          )}
          {created && <Button data-testid="wizard-finalizar" className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={() => onOpenChange(false)}>Finalizar</Button>}
        </div>

        <ContactForm open={contactoDlg} onOpenChange={setContactoDlg} contacto={null} onSaved={vincularContactoNuevo} />
      </DialogContent>
    </Dialog>
  );
}
